import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCurrency } from '@/App';
import { DollarSign, ShoppingCart, Package, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import {
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart, // Usaremos AreaChart en lugar de LineChart para un mejor look en el dashboard
  Area
} from 'recharts';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

// Color principal del gráfico (análogo a hsl(var(--primary)))
const PRIMARY_COLOR = 'hsl(var(--primary))'; 
const CHART_COLORS = ['#BFA774', '#8FA58A', '#E6C9BE', '#262626'];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatPrice } = useCurrency();
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      const [salesRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/reports/sales`),
        axios.get(`${API}/products/low-stock`)
      ]);
      
      setStats(salesRes.data);
      setLowStockProducts(lowStockRes.data.products);
    } catch (error) {
      console.error('Error al cargar datos del panel', error);
    } finally {
      setLoading(false);
    }
  };

  // --- PREPARACIÓN DE DATOS PARA RECHARTS ---
  
  // 1. Datos para el gráfico de Ingresos Diarios (Line/Area Chart)
  // Recharts necesita un array de objetos: [{ name: 'Feb 1', amount: 150 }, ...]
  const revenueChartData = stats?.daily_breakdown 
    ? Object.entries(stats.daily_breakdown).slice(-7).map(([date, dataObject]) => ({
        date: new Date(date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
        Ingresos: dataObject.revenue_usd  // La clave 'Ingresos' se usará en la propiedad dataKey del Area/Line
      }))
    : [];

  // 2. Datos para el gráfico de Ventas por Categoría (Bar Chart)
  // Recharts necesita un array de objetos: [{ name: 'Perfume', Ventas: 300 }, ...]
  const categoryChartData = stats?.category_sales 
    ? Object.entries(stats.category_sales).map(([category, amount]) => ({
        name: category.charAt(0).toUpperCase() + category.slice(1), // Capitalizar categoría para etiqueta
        Ventas: amount // La clave 'Ventas' se usará en la propiedad dataKey del Bar
      }))
    : [];
  
  // --- FIN DE PREPARACIÓN DE DATOS ---
    // Tooltip personalizado
 const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Aseguramos que el tooltip siempre muestre la etiqueta del dato (Ingresos o Ventas)
      const dataEntry = payload[0];
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-gray-900 mb-1">{label}</p>
          <div className="flex items-center">
            {/* Color del indicador basado en el color del trazo/barra */}
            <div 
              className="w-2 h-2 rounded-full mr-2" 
              style={{ backgroundColor: dataEntry.color }}
            ></div>
            <p className="text-gray-600">
              {/* dataEntry.name será "Ingresos" o "Ventas" */}
              {dataEntry.name}: <span className="font-bold text-gray-900">{formatPrice(dataEntry.value)}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };
  // Se eliminan lineChartData, barChartData, y chartOptions (diseñados para Chart.js)
  
  if (loading || !stats || !lowStockProducts) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando reportes...</p>
        </div>
      </div>
    );
  }


  
  return (
    <div className="space-y-6">
      <div className="bg-hero-soft-champagne -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-8 mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold" data-font="display">Panel Principal</h1>
        <p className="text-muted-foreground mt-2">¡Bienvenido! Aquí está lo que sucede hoy.</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        <Card data-testid="dashboard-revenue-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ingresos Totales</CardTitle>
            <DollarSign className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPrice(stats?.summary['total_margin_usd'] || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">De {stats?.summary['total_sales_count'] || 0} ventas</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ventas Totales</CardTitle>
            <ShoppingCart className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{stats?.summary['total_sales_count'] || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Pedidos completados</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Stock Bajo</CardTitle>
            <Package className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{lowStockProducts.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Necesitan atención</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ticket Promedio</CardTitle>
            <TrendingUp className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-font="display">{formatPrice(stats?.summary['avg_ticket_usd'] || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Por venta</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Ingresos (Últimos 7 Días)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <AreaChart 
                style={{ width: '100%', maxWidth: '700px', maxHeight: '70vh', aspectRatio: 1.618 }}
                data={revenueChartData}
                responsive
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.2} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} 
                />
                <YAxis 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="Ingresos" 
                  stroke={CHART_COLORS[0]} 
                  strokeWidth={2}
                  fill="url(#colorAmount)" 
                />
              </AreaChart>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Ventas por Categoría</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <BarChart 
                style={{ width: '100%', maxWidth: '700px', maxHeight: '70vh', aspectRatio: 1.618 }}
                responsive
                data={categoryChartData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.2} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} 
                />
                <YAxis 
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="Ventas" 
                  fill={CHART_COLORS[1]} 
                  radius={[8, 8, 0, 0]} 
                />
              </BarChart>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle data-font="display">Alerta de Stock Bajo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lowStockProducts.slice(0, 5).map((product) => (
                <div key={product.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-muted-foreground">{product.sku}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant={product.stock === 0 ? "destructive" : "secondary"}>
                      {product.stock} unidades
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">Mín: {product.min_stock}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
